import { z } from 'zod';
import {
  BUTTON_TARGET_TYPES,
  BUTTON_VARIANT_TYPES,
  CALLOUT_VARIANT_VALUES,
  COLUMN_VERTICAL_ALIGN_VALUES,
  EMBED_PROVIDER_VALUES,
  FLEX_DIRECTION_VALUES,
  FONT_STYLE_VALUES,
  OBJECT_FIT_VALUES,
  OVERFLOW_VALUES,
  SECTION_BG_SIZE_VALUES,
  SECTION_BG_TYPE_VALUES,
  SECTION_VERTICAL_ALIGN_VALUES,
  SIGNATURE_LAYOUT_VALUES,
  SIGNATURE_VARIANT_VALUES,
  STAT_LAYOUT_VALUES,
  STEPS_CONNECTOR_TYPE_VALUES,
  STEPS_LAYOUT_VALUES,
  STEPS_STEP_SIZE_VALUES,
  TABLE_CAPTION_POSITION_VALUES,
  TABLE_DENSITY_VALUES,
  TABLE_VERTICAL_ALIGN_VALUES,
  TEXT_ALIGN_VALUES,
  TEXT_TRANSFORM_VALUES,
} from './constants';

// ─────────────────────────────────────────────────────────────────
// Block style — per-block CSS overrides
// ─────────────────────────────────────────────────────────────────
export const BlockStyleSchema = z
  .object({
    color: z.string().optional(),
    backgroundColor: z.string().optional(),
    backgroundGradient: z.string().optional(),
    paddingTop: z.string().optional(),
    paddingBottom: z.string().optional(),
    paddingLeft: z.string().optional(),
    paddingRight: z.string().optional(),
    marginTop: z.string().optional(),
    marginBottom: z.string().optional(),
    marginLeft: z.string().optional(),
    marginRight: z.string().optional(),
    textAlign: z.enum(TEXT_ALIGN_VALUES).optional(),
    fontFamily: z.string().optional(),
    fontStyle: z.enum(FONT_STYLE_VALUES).optional(),
    fontSize: z.string().optional(),
    fontWeight: z.string().optional(),
    lineHeight: z.string().optional(),
    flexDirection: z.enum(FLEX_DIRECTION_VALUES).optional(),
    textTransform: z.enum(TEXT_TRANSFORM_VALUES).optional(),
    letterSpacing: z.string().optional(),
    boxShadow: z.string().optional(),
    borderRadius: z.string().optional(),
    borderTopRightRadius: z.string().optional(),
    borderTopLeftRadius: z.string().optional(),
    borderBottomRightRadius: z.string().optional(),
    borderBottomLeftRadius: z.string().optional(),
    border: z.string().optional(),
    borderColor: z.string().optional(),
    gap: z.string().optional(),
    objectFit: z.enum(OBJECT_FIT_VALUES).optional(),
    maxHeight: z.string().optional(),
    minHeight: z.string().optional(),
    maxWidth: z.string().optional(),
    minWidth: z.string().optional(),
    height: z.string().optional(),
    width: z.string().optional(),
    overflow: z.enum(OVERFLOW_VALUES).optional(),
  })
  .strip();

export type BlockStyle = z.infer<typeof BlockStyleSchema>;

// ─────────────────────────────────────────────────────────────────
// Block types
// ─────────────────────────────────────────────────────────────────

// ─────────────────────────────────────────────────────────────────
// LAYOUT & STRUCTURE
// ─────────────────────────────────────────────────────────────────

/**
 * Full-width container with background image / gradient / video + overlay.
 * Use for hero sections, pull-quotes, and editorial chapter breaks.
 */
export const SectionBlockSchema = z.object({
  id: z.string(),
  type: z.literal('section'),
  content: z.string().default(''),
  attrs: z.object({
    backgroundType: z.enum(SECTION_BG_TYPE_VALUES).default('color'),
    backgroundSrc: z.string().optional(), // resolved asset path or URL
    backgroundSrcIsMustache: z.boolean().default(false),
    skipOptimize: z.boolean().default(false),
    backgroundPosition: z.string().default('center center'),
    backgroundSize: z.enum(SECTION_BG_SIZE_VALUES).default('cover'),
    backgroundFixed: z.boolean().default(false), // parallax scroll effect
    overlayColor: z.string().optional(), // e.g. "rgba(0,0,0,0.45)"
    minHeight: z.string().optional(), // e.g. "60vh" | "400px"
    maxWidth: z.string().optional(), // e.g. "900px" — content column width
    verticalAlign: z.enum(SECTION_VERTICAL_ALIGN_VALUES).default('center'),
    paddingX: z.string().default('2rem'),
    paddingY: z.string().default('4rem'),
    reference: z.string().optional(),
    blocks: z.array(z.unknown()).default([]), // nested blocks (lazy, avoids circularity)
  }),
  style: BlockStyleSchema.optional(),
});

export const ListBlockSchema = z.object({
  id: z.string().min(1),
  type: z.literal('list'),
  attrs: z.object({
    /** The template one item is rendered from, authored once. */
    itemBlock: z.unknown(),
    /**
     * The list variable this block binds to. It must name a definition of type
     * `list` on the document; anything else is a broken binding, which the
     * canvas reports rather than rendering.
     */
    dataBindingKey: z.string(),
    dividerType: z.enum(['none', 'line']).default('none'),
    dividerColor: z.string().optional(),
    /**
     * Render-time only: one resolved copy of `itemBlock` per item, filled by the
     * build engine when it renders the block — the bound list's values for a
     * send, its sample item (`default[0]`) for a preview or any build without
     * values. An empty array means "no items", which renders nothing.
     *
     * A stored document never carries this: it keeps the template, and the
     * expansion belongs to the render that needed it. Each entry is a resolved
     * block, or the blocks of a template authored as more than one.
     */
    items: z.array(z.unknown()).optional(),
  }),

  // Not read
  style: BlockStyleSchema.optional(),
});

/**
 * Collapsible FAQ / spoiler panels.
 * Each item has a title and nested block array for the body.
 */
export const AccordionBlockSchema = z.object({
  id: z.string(),
  type: z.literal('accordion'),
  content: z.string().default(''),
  attrs: z.object({
    allowMultiple: z.boolean().default(false), // whether multiple panels can be open
    items: z
      .array(
        z.object({
          id: z.string(),
          title: z.string(),
          section: SectionBlockSchema,
          defaultOpen: z.boolean().default(false),
        }),
      )
      .default([]),
  }),
  style: BlockStyleSchema.optional(),
});

/**
 * Tabbed content panels — great for "before/after", multi-topic explainers.
 */
export const TabsBlockSchema = z.object({
  id: z.string(),
  type: z.literal('tabs'),
  content: z.string().default(''),
  attrs: z.object({
    defaultTab: z.number().int().default(0),
    variant: z.enum(['underline', 'pills', 'boxed']).default('underline'),
    tabs: z
      .array(
        z.object({
          id: z.string(),
          label: z.string(),
          icon: z.string().optional(), // emoji or icon name
          blocks: z.array(z.unknown()).default([]),
        }),
      )
      .default([]),
  }),
  style: BlockStyleSchema.optional(),
});

// ─────────────────────────────────────────────────────────────────
// TYPOGRAPHY & EDITORIAL
// ─────────────────────────────────────────────────────────────────

/**
 * Auto-generated table of contents derived from heading block anchors.
 * Rendered as a sticky sidebar or inline card.
 */
export const TocBlockSchema = z.object({
  id: z.string(),
  type: z.literal('toc'),
  content: z.string().default(''),
  attrs: z.object({
    title: z.string().default('In this article'),
    maxDepth: z.union([z.literal(2), z.literal(3), z.literal(4)]).default(3),
    style: z.enum(['plain', 'card', 'sidebar']).default('card'),
    collapsible: z.boolean().default(false),
  }),
  style: BlockStyleSchema.optional(),
});

/**
 * Inline footnote reference (renders superscript number in flow)
 * paired with a definition rendered at the bottom of the article.
 */
export const FootnoteBlockSchema = z.object({
  id: z.string(),
  type: z.literal('footnote'),
  content: z.string(), // the footnote body — shown at end of article
  attrs: z.object({
    number: z.number().int().min(1), // auto-assigned by the renderer
    referenceId: z.string(), // ID of the inline <sup> anchor in a paragraph
  }),
  style: BlockStyleSchema.optional(),
});

/**
 * Horizontally scrolling ticker — ideal for sponsor logos,
 * category labels, or "as seen in" strips.
 */
export const MarqueeBlockSchema = z.object({
  id: z.string(),
  type: z.literal('marquee'),
  content: z.string().default(''),
  attrs: z.object({
    items: z
      .array(
        z.object({
          label: z.string().optional(),
          imageSrc: z.string().optional(), // logo or icon
          href: z.string().url().optional(),
        }),
      )
      .default([]),
    speed: z.number().default(40), // pixels per second
    direction: z.enum(['left', 'right']).default('left'),
    pauseOnHover: z.boolean().default(true),
    gap: z.string().default('3rem'),
  }),
  style: BlockStyleSchema.optional(),
});

/**
 * Big number + label — great for "by the numbers" sections.
 * Supports animated count-up on scroll.
 */
export const StatBlockSchema = z.object({
  id: z.string(),
  type: z.literal('stat'),
  content: z.string().default(''),
  attrs: z.object({
    stats: z
      .array(
        z.object({
          value: z.string(), // e.g. "12,000" | "94%" | "$3.2B"
          label: z.string(),
          description: z.string().optional(),
          icon: z.string().optional(),
        }),
      )
      .default([]),
    layout: z.enum(STAT_LAYOUT_VALUES).default('row'),
    animateOnScroll: z.boolean().default(true),
  }),
  style: BlockStyleSchema.optional(),
});

/**
 * Steps / Process flow — great for "how it works" sections, tutorials, or onboarding flows.
 * Supports vertical and horizontal layouts with optional icons and numbered steps.
 */
const StepColorsSchema = z.object({
  /** Number badges & CTA links. */
  accent: z.string().optional(),
  /** Icon circle background. */
  indicatorBg: z.string().optional(),
  /** Connector lines. */
  connector: z.string().optional(),
  /** Step titles + block heading. */
  title: z.string().optional(),
  /** Step descriptions. */
  description: z.string().optional(),
});

const StepCtaSchema = z.object({
  text: z.string().optional(),
  url: z.string().optional(),
});

export const StepsSchema = z.object({
  id: z.string(),
  type: z.literal('steps'),
  content: z.string().default(''), // Optional heading/title above steps
  attrs: z.object({
    steps: z
      .array(
        z.object({
          title: z.string(),
          description: z.string().optional(),
          icon: z.string().optional(), // Icon name or emoji
          cta: StepCtaSchema.optional(),
        }),
      )
      .default([]),
    layout: z.enum(STEPS_LAYOUT_VALUES).default('vertical'),
    showNumbers: z.boolean().default(true),
    showConnectors: z.boolean().default(true),
    connectorType: z.enum(STEPS_CONNECTOR_TYPE_VALUES).default('line'),
    stepSize: z.enum(STEPS_STEP_SIZE_VALUES).default('md'),
    /**
     * Per-block color overrides for the steps' internal parts (indicators,
     * connectors, text). Falls back to the design tokens/theme when unset.
     */
    colors: StepColorsSchema.optional(),
  }),
  style: BlockStyleSchema.optional(),
});

/**
 * Syntax-highlighted code block.
 */
export const CodeBlockSchema = z.object({
  id: z.string(),
  type: z.literal('code'),
  content: z.string(), // raw code
  attrs: z.object({
    language: z.string().default('plaintext'), // e.g. "typescript" | "bash" | "json"
    filename: z.string().optional(), // shown as a tab label
    showLineNumbers: z.boolean().default(true),
    highlightLines: z.array(z.number()).default([]), // 1-indexed lines to highlight
    theme: z.enum(['dark', 'light', 'auto']).default('auto'),
    copyButton: z.boolean().default(true),
  }),
  style: BlockStyleSchema.optional(),
});

// ─────────────────────────────────────────────────────────────────
// MEDIA
// ─────────────────────────────────────────────────────────────────

/**
 * Native <video> block — self-hosted or external URL.
 */
export const VideoBlockSchema = z.object({
  id: z.string(),
  type: z.literal('video'),
  content: z.string().default(''),
  attrs: z.object({
    src: z.string(),
    poster: z.string().optional(), // thumbnail shown before play
    caption: z.string().optional(),
    autoplay: z.boolean().default(false),
    loop: z.boolean().default(false),
    muted: z.boolean().default(false),
    controls: z.boolean().default(true),
    aspectRatio: z.string().default('16/9'),
    width: z.number().optional(),
  }),
  style: BlockStyleSchema.optional(),
});

/**
 * Audio player — podcast clips, ambient sound, voice memos.
 */
export const AudioBlockSchema = z.object({
  id: z.string(),
  type: z.literal('audio'),
  content: z.string().default(''),
  attrs: z.object({
    src: z.string(),
    title: z.string().optional(),
    artist: z.string().optional(),
    coverSrc: z.string().optional(),
    autoplay: z.boolean().default(false),
    loop: z.boolean().default(false),
  }),
  style: BlockStyleSchema.optional(),
});

/**
 * Image gallery — grid, masonry, or strip layout.
 * Each image references an asset by ID or direct src.
 */
export const GalleryBlockSchema = z.object({
  id: z.string(),
  type: z.literal('gallery'),
  content: z.string().default(''),
  attrs: z.object({
    images: z
      .array(
        z.object({
          assetId: z.string().optional(),
          src: z.string(),
          alt: z.string().default(''),
          caption: z.string().optional(),
          width: z.number().optional(),
          height: z.number().optional(),
        }),
      )
      .default([]),
    layout: z.enum(['grid', 'masonry', 'strip']).default('grid'),
    columns: z.number().int().min(2).max(5).default(3),
    gap: z.string().default('0.5rem'),
    lightbox: z.boolean().default(true),
    caption: z.string().optional(), // overall gallery caption
  }),
  style: BlockStyleSchema.optional(),
});

// ─────────────────────────────────────────────────────────────────
// DATA
// ─────────────────────────────────────────────────────────────────

/**
 * Data table — comparisons, pricing, specs, schedules, anything tabular.
 *
 * Cells are plain strings that may contain the same inline HTML the rest of a
 * block allows (`strong`, `em`, `a`, `code`, `br`). Renderers sanitise them
 * against the theme's rich-text allow-list, so a cell can never smuggle
 * arbitrary markup into published output.
 *
 * The shape is deliberately flat — no nested block trees — because a table is
 * data: it has to survive JSON round-trips through AI drafts and reusable
 * component snapshots unchanged, and every cell must stay editable in a grid.
 *
 * `stickyHeader` and `scrollable` are web-first: email clients cannot be
 * relied on for sticky positioning inside a scrolling wrapper, so the email
 * renderer pins the table to a fixed layout and inlines striping and borders
 * instead (see themes/minimal/renderer.js → emailTable).
 */
export const TableBlockSchema = z.object({
  id: z.string(),
  type: z.literal('table'),
  content: z.string().default(''), // optional heading above the table
  attrs: z.object({
    /** Column headers, left to right. Empty means a headerless table. */
    headers: z.array(z.string()).default([]),
    /**
     * Body rows; `rows[i][j]` is the cell in row i, column j. Renderers pad
     * short rows and ignore cells past the widest row, so a jagged array
     * still renders as a rectangle.
     */
    rows: z.array(z.array(z.string())).default([]),
    /** Footer rows (totals, sources) — rendered as `<tfoot>`, emphasised. */
    footerRows: z.array(z.array(z.string())).default([]),
    /** First cell of every body row is a row header (`<th scope="row">`). */
    headerColumn: z.boolean().default(false),
    caption: z.string().optional(),
    captionPosition: z.enum(TABLE_CAPTION_POSITION_VALUES).default('top'),
    striped: z.boolean().default(true),
    bordered: z.boolean().default(false),
    /** Web only — keeps the header row visible while the body scrolls. */
    stickyHeader: z.boolean().default(false),
    /** Web only — wraps the table in a horizontal scroll container. */
    scrollable: z.boolean().default(true),
    density: z.enum(TABLE_DENSITY_VALUES).default('normal'),
    verticalAlign: z.enum(TABLE_VERTICAL_ALIGN_VALUES).default('middle'),
    /** Per-column horizontal alignment; a missing entry falls back to `left`. */
    align: z
      .array(
        // per-column alignment
        z.enum(['left', 'center', 'right']),
      )
      .default([]),
    /** Per-column CSS width, e.g. `'25%'`, `'120px'`, `'auto'`. */
    columnWidths: z.array(z.string()).default([]),
    /** Colour overrides — each one falls back to a token/theme default. */
    colors: z
      .object({
        headerBg: z.string().optional(),
        headerText: z.string().optional(),
        border: z.string().optional(),
        stripe: z.string().optional(),
        caption: z.string().optional(),
      })
      .optional(),
  }),
  style: BlockStyleSchema.optional(),
});

/**
 * Inline chart — bar, line, or pie.
 * Data is stored inline; no external data source required.
 */
export const ChartBlockSchema = z.object({
  id: z.string(),
  type: z.literal('chart'),
  content: z.string().default(''),
  attrs: z.object({
    chartType: z.enum(['bar', 'line', 'pie', 'donut', 'area']).default('bar'),
    title: z.string().optional(),
    caption: z.string().optional(),
    labels: z.array(z.string()).default([]), // x-axis or pie slice labels
    datasets: z
      .array(
        z.object({
          label: z.string(),
          data: z.array(z.number()),
          color: z.string().optional(), // CSS color
        }),
      )
      .default([]),
    xAxisLabel: z.string().optional(),
    yAxisLabel: z.string().optional(),
    aspectRatio: z.string().default('16/9'),
    showLegend: z.boolean().default(true),
    showGrid: z.boolean().default(true),
  }),
  style: BlockStyleSchema.optional(),
});

// ─────────────────────────────────────────────────────────────────
// INTERACTIVE
// ─────────────────────────────────────────────────────────────────

/**
 * Countdown timer to a target date — great for launch announcements.
 */
export const CountdownBlockSchema = z.object({
  id: z.string(),
  type: z.literal('countdown'),
  content: z.string().default(''),
  attrs: z.object({
    targetDate: z.string(), // ISO 8601 datetime string
    label: z.string().optional(), // e.g. "Until launch"
    expiredMessage: z.string().default("We're live!"),
    showDays: z.boolean().default(true),
    showHours: z.boolean().default(true),
    showMinutes: z.boolean().default(true),
    showSeconds: z.boolean().default(true),
  }),
  style: BlockStyleSchema.optional(),
});

// ─────────────────────────────────────────────────────────────────
// BLOG / NEWSLETTER SPECIFIC
// ─────────────────────────────────────────────────────────────────

/**
 * Author byline card — shown at top or bottom of an article.
 */
export const AuthorBlockSchema = z.object({
  id: z.string(),
  type: z.literal('author'),
  content: z.string().default(''), // short bio — HTML
  attrs: z.object({
    name: z.string(),
    avatarSrc: z.string().optional(),
    role: z.string().optional(), // e.g. "Senior Editor"
    bio: z.string().optional(), // plain text fallback
    socials: z
      .array(
        z.object({
          platform: z.enum(['twitter', 'linkedin', 'github', 'website', 'instagram', 'threads']),
          url: z.string().url(),
        }),
      )
      .default([]),
    layout: z.enum(['inline', 'card']).default('inline'),
  }),
  style: BlockStyleSchema.optional(),
});

/**
 * Dedicated email-capture CTA — distinct from a generic form block.
 * Renders a focused one-field subscribe box.
 */
export const NewsletterCtaBlockSchema = z.object({
  id: z.string(),
  type: z.literal('newsletter-cta'),
  content: z.string().default(''),
  attrs: z.object({
    heading: z.string().default('Stay in the loop'),
    subheading: z.string().optional(),
    placeholder: z.string().default('your@email.com'),
    buttonLabel: z.string().default('Subscribe'),
    successMessage: z.string().default("You're in! Check your inbox."),
    provider: z
      .enum(['custom', 'mailchimp', 'convertkit', 'beehiiv', 'substack'])
      .default('custom'),
    actionUrl: z.string().url().optional(), // POST endpoint
    tags: z.array(z.string()).default([]), // provider list tags
    variant: z.enum(['minimal', 'card', 'banner']).default('card'),
    gdprNote: z.string().optional(),
  }),
  style: BlockStyleSchema.optional(),
});

/**
 * Related articles strip — typically rendered at the end of a post.
 */
export const RelatedBlockSchema = z.object({
  id: z.string(),
  type: z.literal('related'),
  content: z.string().default(''),
  attrs: z.object({
    heading: z.string().default('Related articles'),
    articles: z
      .array(
        z.object({
          title: z.string(),
          href: z.string(),
          thumbnailSrc: z.string().optional(),
          category: z.string().optional(),
          readingTime: z.number().optional(), // minutes
          publishedAt: z.string().optional(), // ISO date
          excerpt: z.string().optional(),
        }),
      )
      .default([]),
    layout: z.enum(['grid', 'list']).default('grid'),
    columns: z.number().int().min(2).max(4).default(3),
  }),
  style: BlockStyleSchema.optional(),
});

/**
 * Reading progress indicator — configuration block (no content).
 * Renderer turns it into a sticky top bar or circular widget.
 */
export const ProgressBlockSchema = z.object({
  id: z.string(),
  type: z.literal('progress'),
  content: z.string().default(''),
  attrs: z.object({
    position: z.enum(['top', 'bottom']).default('top'),
    variant: z.enum(['bar', 'circle']).default('bar'),
    color: z.string().optional(),
    showPercentage: z.boolean().default(false),
    height: z.string().default('3px'),
  }),
  style: BlockStyleSchema.optional(),
});

/**
 * Tag / category pill list for the article.
 */
export const TagListBlockSchema = z.object({
  id: z.string(),
  type: z.literal('tag-list'),
  content: z.string().default(''),
  attrs: z.object({
    tags: z
      .array(
        z.object({
          label: z.string(),
          href: z.string().optional(),
          color: z.string().optional(),
        }),
      )
      .default([]),
    variant: z.enum(['pill', 'badge', 'plain']).default('pill'),
  }),
  style: BlockStyleSchema.optional(),
});

/**
 * Social share row — renders platform share buttons for the current URL.
 */
export const SocialShareBlockSchema = z.object({
  id: z.string(),
  type: z.literal('social-share'),
  content: z.string().default(''),
  attrs: z.object({
    platforms: z
      .array(
        z.enum(['twitter', 'linkedin', 'facebook', 'reddit', 'whatsapp', 'email', 'copy-link']),
      )
      .default(['twitter', 'linkedin', 'copy-link']),
    label: z.string().optional(), // e.g. "Share this article"
    variant: z.enum(['icon', 'icon-label', 'label']).default('icon-label'),
    align: z.enum(['left', 'center', 'right']).default('left'),
    shareUrl: z.string().optional(), // override auto-detected URL
    shareTitle: z.string().optional(),
  }),
  style: BlockStyleSchema.optional(),
});

/**
 * Vertical timeline — great for changelogs, roadmaps, event histories.
 */
export const TimelineBlockSchema = z.object({
  id: z.string(),
  type: z.literal('timeline'),
  content: z.string().default(''),
  attrs: z.object({
    orientation: z.enum(['vertical', 'horizontal']).default('vertical'),
    items: z
      .array(
        z.object({
          id: z.string(),
          date: z.string(), // display string e.g. "April 2024"
          isoDate: z.string().optional(),
          title: z.string(),
          blocks: z.array(z.unknown()).default([]),
          icon: z.string().optional(),
          variant: z.enum(['default', 'highlight', 'milestone']).default('default'),
        }),
      )
      .default([]),
    lineStyle: z.enum(['solid', 'dashed', 'dotted']).default('solid'),
    lineColor: z.string().optional(),
  }),
  style: BlockStyleSchema.optional(),
});

/**
 * Generic contact / survey form.
 * For simple email capture, prefer newsletter-cta.
 */
export const FormBlockSchema = z.object({
  id: z.string(),
  type: z.literal('form'),
  content: z.string().default(''),
  attrs: z.object({
    heading: z.string().optional(),
    fields: z
      .array(
        z.object({
          id: z.string(),
          type: z.enum(['text', 'email', 'textarea', 'select', 'checkbox', 'radio', 'number']),
          label: z.string(),
          placeholder: z.string().optional(),
          required: z.boolean().default(false),
          options: z.array(z.string()).default([]), // for select | radio
          defaultValue: z.string().optional(),
          helpText: z.string().optional(),
          width: z.enum(['full', 'half']).default('full'),
        }),
      )
      .default([]),
    submitLabel: z.string().default('Send'),
    successMessage: z.string().default('Thanks! We got your message.'),
    actionUrl: z.string().url().optional(),
    method: z.enum(['POST', 'GET']).default('POST'),
    honeypot: z.boolean().default(true), // spam protection
  }),
  style: BlockStyleSchema.optional(),
});

export const HeadingBlockSchema = z.object({
  id: z.string(),
  type: z.literal('heading'),
  content: z.string(),
  contentHasVar: z.boolean().default(false),
  attrs: z.object({
    level: z.union([z.literal(1), z.literal(2), z.literal(3), z.literal(4)]),
    anchor: z.string().optional(), // auto-generated from content for ToC
  }),
  style: BlockStyleSchema.optional(),
});

export const ParagraphBlockSchema = z.object({
  id: z.string(),
  type: z.literal('paragraph'),
  content: z.string(), // HTML string from Tiptap
  contentHasVar: z.boolean().default(false),
  attrs: z.object({}).optional(),
  style: BlockStyleSchema.optional(),
});

export const ImageBlockSchema = z.object({
  id: z.string(),
  type: z.literal('image'),
  content: z.string().default(''),
  attrs: z.object({
    assetId: z.string().optional(),
    src: z.string(), // resolved relative path for output: ./assets/img.webp
    srcIsMustache: z.boolean().default(false),
    alt: z.string().default(''),
    caption: z.string().optional(),
    width: z.number().optional(),
    skipOptimize: z.boolean().default(false),
    fullWidth: z.boolean().default(false),
    fullHeight: z.boolean().default(false),
    height: z.number().optional(),
    sizes: z.string().optional(), // srcset sizes attribute
  }),
  style: BlockStyleSchema.optional(),
});

/**
 * A round (or rounded) frame with a picture in it, centred on its row. The
 * frame is `size` px square, shaped by `radius`, and painted by the block's
 * style (background colour, border). `image` holds the same fields as an image
 * block's attrs (keep them in step), minus the layout switches an avatar has
 * no use for: with no `width`/`height` the picture fills the frame (a photo);
 * with them it is that size, centred in the frame (an icon in a badge). With
 * an `href` the whole avatar is a link.
 *
 * `size` is optional only for avatars saved before it existed, whose
 * `image.width` was the frame: renderers read `size ?? image.width ?? 64`.
 *
 * Written out rather than picked from ImageBlockSchema: the skill generator
 * reads this file's syntax and cannot follow `.shape.attrs.pick(...)`.
 */
export const AvatarBlockSchema = z.object({
  id: z.string(),
  type: z.literal('avatar'),
  content: z.string().default(''),
  attrs: z.object({
    image: z.object({
      assetId: z.string().optional(),
      src: z.string(),
      srcIsMustache: z.boolean().default(false),
      alt: z.string().default(''),
      skipOptimize: z.boolean().default(false),
      width: z.number().int().min(1).max(600).optional(), // unset = fill the frame
      height: z.number().int().min(1).max(600).optional(), // unset = width, or fill
    }),
    size: z.number().int().min(16).max(600).optional(),
    radius: z.string().default('50%'),
    href: z.string().optional(),
    hrefIsMustache: z.boolean().default(false),
  }),
  style: BlockStyleSchema.optional(),
});

export const QuoteBlockSchema = z.object({
  id: z.string(),
  type: z.literal('quote'),
  content: z.string(),
  contentHasVar: z.boolean().default(false),
  attrs: z
    .object({
      citation: z.string().optional(),
    })
    .optional(),
  style: BlockStyleSchema.optional(),
});

export const CalloutBlockSchema = z.object({
  id: z.string(),
  type: z.literal('callout'),
  content: z.string(),
  contentHasVar: z.boolean().default(false),
  attrs: z.object({
    variant: z.enum(CALLOUT_VARIANT_VALUES).default('info'),
    icon: z.string().optional(),
  }),
  style: BlockStyleSchema.optional(),
});

export const DividerBlockSchema = z.object({
  id: z.string(),
  type: z.literal('divider'),
  content: z.string().default(''),
  attrs: z.object({}).optional(),
  style: BlockStyleSchema.optional(),
});

export const ButtonBlockSchema = z.object({
  id: z.string(),
  type: z.literal('button'),
  content: z.string(), // button label text
  attrs: z.object({
    href: z.string(),
    hrefIsMustache: z.boolean().default(false),
    trackHref: z.boolean().default(false),
    target: z.enum(BUTTON_TARGET_TYPES).default('_self'),
    variant: z.enum(BUTTON_VARIANT_TYPES).default('primary'),
  }),
  style: BlockStyleSchema.optional(),
});

export const EmbedBlockSchema = z.object({
  id: z.string(),
  type: z.literal('embed'),
  content: z.string().default(''),
  attrs: z.object({
    url: z.string().url(),
    provider: z.enum(EMBED_PROVIDER_VALUES),
    embedHtml: z.string().optional(), // resolved oEmbed HTML
    aspectRatio: z.string().default('16/9'),
  }),
  style: BlockStyleSchema.optional(),
});

export const HtmlBlockSchema = z.object({
  id: z.string(),
  type: z.literal('html'),
  content: z.string(), // raw HTML — sanitised by DOMPurify on server
  attrs: z
    .object({
      cssStr: z.string().optional(),
      isHeader: z.boolean().optional(),
      isFooter: z.boolean().optional(),
    })
    .optional(),
  style: BlockStyleSchema.optional(),
});

export const OtpBlockSchema = z.object({
  id: z.string(),
  type: z.literal('otp'),
  content: z.string().default(''),
  attrs: z.object({
    code: z.string().optional(), // resolved at send time
    codeIsMustache: z.boolean().default(false),
    length: z.number().int().min(4).max(8).default(6),
    groupSize: z.number().int().min(0).max(4).default(3), // 0 = no grouping
    label: z.string().default('Your verification code'),
    boxed: z.boolean().default(true),
  }),
  style: BlockStyleSchema.optional(),
});

// Columns block — contains nested block arrays
export const ColumnsBlockSchema = z.object({
  id: z.string(),
  type: z.literal('columns'),
  content: z.string().default(''),
  attrs: z.object({
    shouldWrapOnMobile: z.boolean().default(true),
    columns: z.number().int().min(1).default(1),
    gap: z.string().default('2rem'),
    verticalAlign: z.enum(COLUMN_VERTICAL_ALIGN_VALUES).default('top'),
    /**
     * The length each column is pinned to, index-aligned with `columnBlocks`:
     * `'160px'` / `'30%'` keeps that column at that size, `null` (or a missing
     * entry — the default) lets it take whatever the pinned columns leave.
     * Read through `parseColumnWidth`, which is the only thing that decides
     * whether a value is usable.
     */
    columnWidths: z.array(z.string().nullable()).default([]),
    // Nested blocks per column — validated lazily to avoid circularity
    columnBlocks: z.array(z.array(z.unknown())).default([[], []]),
  }),
  style: BlockStyleSchema.optional(),
});

// ─────────────────────────────────────────────────────────────────
// Signature block — a person/brand sign-off (email-safe)
// ─────────────────────────────────────────────────────────────────
// Rendered with the signer's name/title as REAL text (never image-only —
// image-only signatures trip spam filters) and alt text on every image.
// The logo image (logoSrc) is a good place for an animated GIF signature;
// it degrades to a static first frame in clients that don't animate.
export const SignatureBlockSchema = z.object({
  id: z.string(),
  type: z.literal('signature'),
  content: z.string().default(''),
  attrs: z.object({
    name: z.string().min(1, 'Signature name is required').default(''),
    title: z.string().optional(),
    company: z.string().optional(),
    avatarSrc: z.string().optional(),
    avatarAlt: z.string().optional(),
    logoSrc: z.string().optional(),
    logoAlt: z.string().optional(),
    socials: z.array(z.object({ platform: z.string(), url: z.string().url() })).default([]),
    variant: z.enum(SIGNATURE_VARIANT_VALUES).default('compact'),
    layout: z.enum(SIGNATURE_LAYOUT_VALUES).default('left'),
  }),
  style: BlockStyleSchema.optional(),
});

// ─────────────────────────────────────────────────────────────────
// Union of all block types
// ─────────────────────────────────────────────────────────────────
export const BlockSchema = z.discriminatedUnion('type', [
  HeadingBlockSchema,
  ParagraphBlockSchema,
  ImageBlockSchema,
  AvatarBlockSchema,
  QuoteBlockSchema,
  CalloutBlockSchema,
  DividerBlockSchema,
  ButtonBlockSchema,
  EmbedBlockSchema,
  HtmlBlockSchema,
  ColumnsBlockSchema,
  SectionBlockSchema,
  AccordionBlockSchema,
  StepsSchema,
  OtpBlockSchema,
  ListBlockSchema,
  // TabsBlockSchema,
  // TocBlockSchema,
  // FootnoteBlockSchema,
  // MarqueeBlockSchema,
  StatBlockSchema,
  SignatureBlockSchema,
  TableBlockSchema,
  // CodeBlockSchema,
  // VideoBlockSchema,
  // AudioBlockSchema,
  // GalleryBlockSchema,
  // ChartBlockSchema,
  // CountdownBlockSchema,
  // AuthorBlockSchema,
  // NewsletterCtaBlockSchema,
  // RelatedBlockSchema,
  // ProgressBlockSchema,
  // TagListBlockSchema,
  // SocialShareBlockSchema,
  // TimelineBlockSchema,
  // FormBlockSchema,
]);

export type Block = z.infer<typeof BlockSchema>;
/**
 * A block as authored, before the schema fills its defaults in: the shape the
 * editor holds while a block is being built (see the editor store's drafts).
 */
export type BlockInput = z.input<typeof BlockSchema>;
export type BlockType = Block['type'];

export type OtpBlock = z.infer<typeof OtpBlockSchema>;
export type HeadingBlock = z.infer<typeof HeadingBlockSchema>;
export type ParagraphBlock = z.infer<typeof ParagraphBlockSchema>;
export type ImageBlock = z.infer<typeof ImageBlockSchema>;
export type AvatarBlock = z.infer<typeof AvatarBlockSchema>;
export type QuoteBlock = z.infer<typeof QuoteBlockSchema>;
export type CalloutBlock = z.infer<typeof CalloutBlockSchema>;
export type DividerBlock = z.infer<typeof DividerBlockSchema>;
export type ButtonBlock = z.infer<typeof ButtonBlockSchema>;
export type EmbedBlock = z.infer<typeof EmbedBlockSchema>;
export type HtmlBlock = z.infer<typeof HtmlBlockSchema>;
export type ColumnsBlock = z.infer<typeof ColumnsBlockSchema>;
export type SectionBlock = z.infer<typeof SectionBlockSchema>;
export type AccordionBlock = z.infer<typeof AccordionBlockSchema>;
export type TabsBlock = z.infer<typeof TabsBlockSchema>;
export type TocBlock = z.infer<typeof TocBlockSchema>;
export type FootnoteBlock = z.infer<typeof FootnoteBlockSchema>;
export type MarqueeBlock = z.infer<typeof MarqueeBlockSchema>;
export type StatBlock = z.infer<typeof StatBlockSchema>;
export type SignatureBlock = z.infer<typeof SignatureBlockSchema>;
export type CodeBlock = z.infer<typeof CodeBlockSchema>;
export type VideoBlock = z.infer<typeof VideoBlockSchema>;
export type AudioBlock = z.infer<typeof AudioBlockSchema>;
export type GalleryBlock = z.infer<typeof GalleryBlockSchema>;
export type TableBlock = z.infer<typeof TableBlockSchema>;
export type ChartBlock = z.infer<typeof ChartBlockSchema>;
export type CountdownBlock = z.infer<typeof CountdownBlockSchema>;
export type AuthorBlock = z.infer<typeof AuthorBlockSchema>;
export type NewsletterCtaBlock = z.infer<typeof NewsletterCtaBlockSchema>;
export type RelatedBlock = z.infer<typeof RelatedBlockSchema>;
export type ProgressBlock = z.infer<typeof ProgressBlockSchema>;
export type TagListBlock = z.infer<typeof TagListBlockSchema>;
export type SocialShareBlock = z.infer<typeof SocialShareBlockSchema>;
export type TimelineBlock = z.infer<typeof TimelineBlockSchema>;
export type FormBlock = z.infer<typeof FormBlockSchema>;
export type StepsBlock = z.infer<typeof StepsSchema>;
export type ListBlock = z.infer<typeof ListBlockSchema>;
